import React, { useEffect, useState, useRef } from 'react'
import { Link } from "react-router-dom"
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import simulate from '../lib/simulate.js'
import ipsc from '../lib/ipsc.js'
import toFixed from '../lib/toFixed.js'

function HitProbability() {
  const profile = {
    azimuth: "0",
    dragCurve: "G7",
    bc: ".3",
    v0: "2800",
    humidity: "50",
    latitude: "45",
    lookAngle: "0",
    outputUnit: "MOA",
    pressure: "29.92",
    scopeHeight: "2",
    temperature: "59",
    twistDirection: "1",
    useAerodynamicJump: true,
    useSpinDrift: true,
    range: "1000",
    rangeStep: "100",
    zeroRange: "100",
    wind: [ { speed: "10", direction: "90" } ]
  }
  const [ showAdvanced, setShowAdvanced ] = useAutoSaveState('showAdvanced', false)
  const [ variables, setVariables ] = useAutoSaveState('variables', null)
  const [ , setPreview ] = useAutoSaveState('preview', null)
  const [ results, setResults ] = useState(null)

  const canvasRef = useRef(null)

  useEffect(() => {
    setInitialVariables(profile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runSimulation = () => {
    const { points1, points2, hits1, hits2, prob1, prob2 } = simulate(profile, variables)
    console.log({ points1, points2, hits1, hits2, prob1, prob2 })
    drawPoints(points1, points2)
    setResults({ prob1, prob2 })
    setPreview(`First Round: ${toFixed(prob1 * 100, 1)}%, Second Round: ${toFixed(prob2 * 100, 1)}%`)
    window.gtag('event', 'calculate-hit-probability')
  }

  const drawPoints = (points1, points2) => {
    const canvas = canvasRef.current
    canvas.height = canvas.offsetHeight
    canvas.width = canvas.offsetWidth
    console.log(canvas)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    ctx.fillStyle = '#999'
    const targetPx = 100
    const target = getTarget()
    const pxScale = targetPx / (target.shape === 'ipsc' ? 18 : target.size)
    if (target.shape === 'circle') {
      ctx.beginPath()
      ctx.arc(ctx.canvas.width / 2, ctx.canvas.height / 2, targetPx / 2, 0, 2 * Math.PI)
      ctx.fill()
    } else if (target.shape === 'square') {
      ctx.fillRect(
        ctx.canvas.width / 2 - targetPx / 2,
        ctx.canvas.height / 2 - targetPx / 2,
        targetPx,
        targetPx
      )
    } else if (target.shape === 'ipsc') {
      ctx.beginPath()
      ctx.moveTo(
        -ipsc[0][0] * pxScale + ctx.canvas.height / 2,
        -ipsc[0][1] * pxScale + ctx.canvas.width / 2
      )
      ipsc.forEach(p => ctx.lineTo(
        -p[0] * pxScale + ctx.canvas.height / 2,
        -p[1] * pxScale + ctx.canvas.width / 2
      ))
      ctx.fill()
    }
    const drawImpact = ([drift, drop]) => {
      const gx = -drift * pxScale + ctx.canvas.width / 2
      const gy = -drop * pxScale + ctx.canvas.height / 2
      ctx.beginPath()
      ctx.arc(gx, gy, 1.5, 0, 2 * Math.PI)
      ctx.fill()
    }
    ctx.fillStyle = '#f00'
    points1.forEach(drawImpact)
    ctx.fillStyle = '#00f'
    points2.forEach(drawImpact)
  }

  const getTarget = () => {
    const targetVariable = variables.find(variable => variable.name === 'target')
    return targetVariable
  }

  const setInitialVariables = profile => {
    const variables = [
      getDefaultVariableSetting('target', profile),
      getDefaultVariableSetting('iterations', profile),
      getDefaultVariableSetting('groupSize', profile),
      getDefaultVariableSetting('v0', profile),
      getDefaultVariableSetting('bc', profile),
      getDefaultVariableSetting('range', profile),
      getDefaultVariableSetting('windSpeed', profile),
      getDefaultVariableSetting('windDirection', profile),
      getDefaultVariableSetting('verticalZero', profile),
      getDefaultVariableSetting('horizontalZero', profile),
      getDefaultVariableSetting('useAerodynamicJump', profile),
      getDefaultVariableSetting('useSpinDrift', profile),
      getDefaultVariableSetting('latitude', profile),
      getDefaultVariableSetting('azimuth', profile),
      getDefaultVariableSetting('humidity', profile),
      getDefaultVariableSetting('lookAngle', profile),
      getDefaultVariableSetting('cant', profile),
      getDefaultVariableSetting('pressure', profile),
      getDefaultVariableSetting('temperature', profile),
      getDefaultVariableSetting('scopeHeight', profile),
    ]
    setVariables(variables)
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={1} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" style={{ textAlign: 'center' }}>
            Hit Probability Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={12} mt={2} />
        <Grid item xs={12} md={6}>
          <Grid container spacing={1} justifyContent="center" >
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdvanced}
                    onChange={e => setShowAdvanced(e.target.checked)}
                  />}
                label="Show Advanced Options"
              />
            </Grid>
            {variables && variables.map((variable, i) => ((!variable.advanced || showAdvanced) && <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Variable
                  {...variable}
                  set={(key, value) => {
                    const newVariables = [ ...variables ]
                    newVariables[i] = { ...variable, [key]: value }
                    console.log({ variables, newVariables, key, value })
                    if (variable.name === 'groupSize' && key === 'actual') {
                      const sd = (value / 2).toFixed(2)
                      if (!isNaN(sd)) {
                        newVariables[i].sd = sd
                      }
                    }
                    if (variable.name === 'groupSize' && key === 'sd') {
                      const actual = (value * 2).toFixed(2)
                      if (!isNaN(actual)) {
                        newVariables[i].actual = actual
                      }
                    }
                    setVariables(newVariables)
                  }}
                />
              </Paper>
            </Grid>))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper>
            <Box p={1}>
              <Button
                variant="contained"
                fullWidth
                onClick={runSimulation}
              >
                Run Simulation
              </Button>
            </Box>
            <Box p={1} pt={0}>
              <canvas ref={canvasRef} style={{ border: '1px solid black', width: '100%', aspectRatio: '1 / 1' }} />
            </Box>
            {results && <Box p={1} pt={0}>
              <Typography variant="h5">
                First Round Hit Probability: {toFixed(results.prob1 * 100, 1)}%
              </Typography>
              <Typography variant="h5">
                Second Round Hit Probability: {toFixed(results.prob2 * 100, 1)}%
              </Typography>
            </Box>}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}

function Variable ({ name, measured, actual, sd, required, units, using, size, shape, set, useSd, actualLabel }) {
  let columns = 3
  if (typeof sd !== 'string' && typeof measured !== 'string') columns = 9
  else if (typeof sd !== 'string' || typeof measured !== 'string') columns = 4
  return <Grid container columnSpacing={2}>
    <Grid xs={12} sm={3} container alignItems="center" pl={2}>
      <Typography variant="h5">
        {toChipLabel(name)}
      </Typography>
    </Grid>
    {typeof size === 'string' && <Grid item xs={12} sm={4}>
      <TextField disabled={shape === 'ipsc'} fullWidth label="Size" value={shape === 'ipsc' ? '18' : size} onChange={e => set('size', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }} />
    </Grid>}
    {typeof shape === 'string' && <Grid item xs={12} sm={4}>
      <FormControl fullWidth>
        <InputLabel>Shape</InputLabel>
        <Select
          fullWidth
          value={shape}
          label="Shape"
          onChange={e => set('shape', e.target.value)}
        >
          <MenuItem value="circle">Circle</MenuItem>
          <MenuItem value="square">Square</MenuItem>
          <MenuItem value="ipsc">IPSC</MenuItem>
        </Select>
      </FormControl>
    </Grid>}
    {typeof measured === 'string' && <Grid item xs={12} sm={columns}>
      <TextField fullWidth label="Measured" value={measured} onChange={e => set('measured', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }} />
    </Grid>}
    {typeof actual === 'string' && <Grid item xs={12} sm={columns}>
      <TextField
        fullWidth
        label={actualLabel ? actualLabel : 'Actual'}
        disabled={useSd === true}
        value={actual}
        onChange={e => set('actual', e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }}
      />
    </Grid>}
    {typeof useSd === 'boolean' && <Grid container item xs={1} justifyContent="center" alignItems="center">
        <Switch color="default" checked={useSd} onChange={e => set('useSd', e.target.checked)} />
    </Grid>}
    {typeof sd === 'string' && <Grid item xs={12} sm={columns}>
      <TextField fullWidth label="Standard Deviation" value={sd} disabled={useSd === false} onChange={e => set('sd', e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{units}</InputAdornment> }} />
    </Grid>}
    {typeof using === 'boolean' && <Grid item xs={12} sm={columns}>
      <FormControlLabel
        control={<Switch checked={using} onChange={e => set('using', e.target.checked)} />}
        label={`Account for ${toChipLabel(name)}`}
      />
    </Grid>}
  </Grid>
}

function ProfileChooser ({ profileName, setProfile, profiles, profilesExpanded, setProfilesExpanded }) {
  let profileNames = []
  try {
    profileNames = Object.keys(profiles)
  } catch (e) { }

  return <Accordion expanded={profilesExpanded} onClick={() => profileName && setProfilesExpanded(!profilesExpanded)}>
    <AccordionSummary
      expandIcon={profileName ? <ExpandMoreIcon /> : null}
      aria-controls="panel1a-content"
      id="panel1a-header"
    >
      <Grid container spacing={2}>
        <Grid xs={12} container mt={2} justifyContent="center">
          <Typography variant="h4" component="h2">
            {profileName
              //? `${profileName} Hit Probability`
                ? `Hit Probability: ${profileName}`
              : ((profiles && profileNames.length > 0)
                ? 'Choose a Profile'
                : 'Add a Profile First'
              )
            }
          </Typography>
        </Grid>
      </Grid>
    </AccordionSummary>
    <AccordionDetails>
      {(profiles && profileNames.length > 0)
        ? profileNames.map(name => {
          const profile = profiles[name]
          return <Box mb={2}>
            <Card variant="outlined">
              <CardActionArea onClick={() => {
                setProfile(name)
              }}>
                <CardContent>
                  <Typography variant="h4" component="div">
                    {name}
                  </Typography>
                  <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap" }}>
                    {Object.keys(profile).filter(key => profile[key]).map(key => (
                      <Chip
                        size="small"
                        sx={{ margin: '2px' }}
                        label={`${toChipLabel(key)}: ${toChipValue(key, profile[key])}`}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        })
        : <Typography>
          Please go to the <Link to="/tools/ballistic-calculator">ballistics calculator</Link> and add a profile to use the hit probability tool
        </Typography>
      }
    </AccordionDetails>
  </Accordion>
}

function toChipLabel (key, value) {
	if (key === 'bc') {
    key = 'BC'
  } else if (key === 'v0') {
    key = 'Muzzle Velocity'
  } else {
    key = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
  }
  key = key.replace('Use ', '')
  return key
}

function toChipValue (key, value) {
  if (key === 'wind') {
    return `${value[0].speed}mph @ ${value[0].direction}deg`
  }
  return value
}

function getDefaultVariableSetting (name, profile) {
  switch (name) {
    case  'target':              return  {  name, size:      '20',                        shape:     'circle',                   units: 'in',                                                       }
    case  'iterations':          return  {  name,                                         actual:    '1000',                     actualLabel: 'Iterations',                                         }
    case  'groupSize':           return  {  name, actualLabel: 'Avg Five Shot Group',     actual:  '1',                          units: 'moa',   sd: '.5',       useSd: false,                      }
    case  'verticalZero':        return  {  name,                                         actual:    '0',                        units: 'moa',   actualLabel: 'Vertical Zero',       advanced: true }
    case  'horizontalZero':      return  {  name,                                         actual:    '0',                        units: 'moa',   actualLabel: 'Horizontal Zero',     advanced: true }
    case  'useAerodynamicJump':  return  {  name,                                         using:     profile.useAerodynamicJump,                                                     advanced: true }
    case  'useSpinDrift':        return  {  name,                                         using:     profile.useSpinDrift,                                                           advanced: true }
    case  'latitude':            return  {  name,  measured:  profile.latitude,           actual:    profile.latitude,           units: 'deg',                                       advanced: true }
    case  'v0':                  return  {  name,  measured:  profile.v0,                 actual:    profile.v0,                 units: 'fps',   sd:  '10',                                         }
    case  'bc':                  return  {  name,  measured:  profile.bc,                 actual:    profile.bc,                                 sd:  (profile.bc * .01).toFixed(4)                 }
    case  'range':               return  {  name,  measured:  profile.range,              actual:    profile.range,              units: 'yards', sd:  '3',                                          }
    case  'azimuth':             return  {  name,  measured:  profile.azimuth,            actual:    profile.azimuth,            units: 'deg',   sd:  '2' ,                          advanced: true }
    case  'humidity':            return  {  name,  measured:  profile.humidity,           actual:    profile.humidity,           units: '%',     sd:  '10',                          advanced: true }
    case  'lookAngle':           return  {  name,  measured:  profile.lookAngle,          actual:    profile.lookAngle,          units: 'deg',   sd:  '2',                           advanced: true }
    case  'cant':                return  {  name,  measured:  '0',                        actual:    '0',                        units: 'deg',   sd:  '0',                           advanced: true }
    case  'pressure':            return  {  name,  measured:  profile.pressure,           actual:    profile.pressure,           units: 'inHg',  sd:  '.1',                          advanced: true }
    case  'temperature':         return  {  name,  measured:  profile.temperature,        actual:    profile.temperature,        units: '°F',    sd:  '1',                           advanced: true }
    case  'windSpeed':           return  {  name,  measured:  profile.wind[0].speed,      actual:    profile.wind[0].speed,      units: 'mph',   sd:  '2',                                          }
    case  'windDirection':       return  {  name,  measured:  profile.wind[0].direction,  actual:    profile.wind[0].direction,  units: 'deg',   sd:  '5',                                          }
    case  'scopeHeight':         return  {  name,  measured:  profile.scopeHeight,        actual:    profile.scopeHeight,        units: 'in',                                        advanced: true }
    default: throw new Error('unknown variable name')
  }
}

export default function WrappedHitProbability() {
  return (
    <AutoSaveProvider>
      <HitProbability />
    </AutoSaveProvider>
  )
}
